const Config = require('../cubeConfig'),
    Utils = require('../cubeInternalUtils');

const DIALOGS_API_URL = Config.urls.chat + '/Dialog';

function DialogProxy(service) {
    this.service = service;
}

DialogProxy.prototype = {
    /**
     * Retrieve list of dialogs.
     * @memberof CB.chat.dialog
     * @param {Object} params - Some filters to get only chat dialogs you need.
     * @param {listDialogCallback} callback - The callback function.
     * */
    list: function (params, callback) {
        /**
         * Callback for CB.chat.dialog.list().
         * @param {Object} error - The error object
         * @param {Object} resDialogs - the dialog list
         * @callback listDialogCallback
         * */

        if (typeof params === 'function' && typeof callback === 'undefined') {
            callback = params;
            params = {};
        }

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL),
            data: params
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Create new dialog.
     * @memberof CB.chat.dialog
     * @param {Object} params - Object of parameters.
     * @param {createDialogCallback} callback - The callback function.
     * */
    create: function (params, callback) {
        /**
         * Callback for CB.chat.dialog.create().
         * @param {Object} error - The error object
         * @param {Object} createdDialog - the dialog object
         * @callback createDialogCallback
         * */

        if (params && params.occupants_ids && Utils.isArray(params.occupants_ids)) {
            params.occupants_ids = params.occupants_ids.join(', ');
        }

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL),
            type: 'POST',
            data: params
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Update group dialog.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {Object} params - Object of parameters.
     * @param {updateDialogCallback} callback - The callback function.
     * */
    update: function (id, params, callback) {
        /**
         * Callback for CB.chat.dialog.update()
         * @param {Object} error - The error object
         * @param {Object} res - the dialog object
         * @callback updateDialogCallback
         * */

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id),
            type: 'PUT',
            contentType: 'application/json; charset=utf-8',
            isNeedStringify: true,
            data: params,
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Delete a dialog or dialogs.
     * @memberof CB.chat.dialog
     * @param {Array} id - The dialog IDs array.
     * @param {Object | function} params_or_callback - Object of parameters or callback function.
     * @param {deleteDialogCallback} callback - The callback function.
     * */
    delete: function (id, params_or_callback, callback) {
        /**
         * Callback for CB.chat.dialog.delete()
         * @param {Object} error - The error object
         * @callback deleteDialogCallback
         * */

        var ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id),
            type: 'DELETE',
            dataType: 'text'
        };

        if (arguments.length === 2) {
            this.service.ajax(ajaxParams, params_or_callback);
        } else if (arguments.length === 3) {
            ajaxParams.data = params_or_callback;
            this.service.ajax(ajaxParams, callback);
        }
    },

    /**
     * Add admins to dialog.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {Array} admins_ids - Array of admins' IDs to add.
     * @param {addAdminsToDialogCallback} callback - The callback function.
     * */
    addAdmins: function (id, admins_ids, callback) {
        /**
         * Callback for CB.chat.dialog.addAdmins()
         * @param {Object} error - The error object
         * @param {Object} response - the dialog object
         * @callback addAdminsToDialogCallback
         * */

        const params = {
            push_all: {
                admins_ids: admins_ids
            }
        }

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'admins'),
            type: 'PUT',
            contentType: 'application/json; charset=utf-8',
            isNeedStringify: true,
            data: params
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Remove admins from dialog.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {Array} admins_ids - Array of admins' IDs to remove,
     * @param {removeAdminsFromDialogCallback} callback - The callback function.
     * */
    removeAdmins: function (id, admins_ids, callback) {
        /**
         * Callback for CB.chat.dialog.removeAdmins()
         * @param {Object} error - The error object
         * @param {Object} response - the dialog object
         * @callback removeAdminsFromDialogCallback
         * */

        const params = {
            pull_all: {
                admins_ids: admins_ids
            }
        }

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'admins'),
            type: 'PUT',
            contentType: 'application/json; charset=utf-8',
            isNeedStringify: true,
            data: params
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Subscribe to public dialog.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {subscribeToPublicDialogCallback} callback - The callback function.
     * */
    subscribeToPublic: function (id, callback) {
        /**
         * Callback for CB.chat.dialog.subscribeToPublic()
         * @param {Object} error - The error object
         * @param {Object} response - the dialog object
         * @callback subscribeToPublicDialogCallback
         * */

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'subscribe'),
            type: 'POST'
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Unubscribe from public dialog.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {unsubscribeFromPublicDialogCallback} callback - The callback function.
     * */
    unsubscribeFromPublic: function (id, callback) {
        /**
         * Callback for CB.chat.dialog.unsubscribeFromPublic()
         * @param {Object} error - The error object
         * @param {Object} response - the dialog object
         * @callback unsubscribeFromPublicDialogCallback
         * */

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'subscribe'),
            type: 'DELETE',
            dataType: 'text'
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Turn on/off push notifications for offline messages in a dialog.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {Number | Boolean} enabled - 0 or 1 (or false/true) to Disable or Enable notifications.
     * @param {updateNotificationsSettingsCallback} callback - The callback function.
     * */
    updateNotificationsSettings: function (id, enabled, callback) {
        /**
         * Callback for CB.chat.dialog.updateNotificationsSettings()
         * @param {Object} error - The error object
         * @param {Object} response - the object with notifications settings
         * @callback updateNotificationsSettingsCallback
         * */

        const settings = {
            enabled: enabled ? 1 : 0
        }

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'notifications'),
            type: 'PUT',
            contentType: 'application/json; charset=utf-8',
            isNeedStringify: true,
            data: settings
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Get a status of notifications setting.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {getNotificationsSettingsCallback} callback - The callback function.
     * */
    getNotificationsSettings: function (id, callback) {
        /**
         * Callback for CB.chat.dialog.getNotificationsSettings()
         * @param {Object} error - The error object
         * @param {Object} response - the object with notifications settings
         * @callback getNotificationsSettingsCallback
         * */

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'notifications'),
            type: 'GET'
        };

        this.service.ajax(ajaxParams, callback);
    },

    /**
     * Get public dialog's occupants.
     * @memberof CB.chat.dialog
     * @param {String} id - The dialog ID.
     * @param {Object} params - Some filters to get only chat dialogs you need.
     * @param {getPublicOccupantsCallback} callback - The callback function.
     * */
    getPublicOccupants: function (id, params, callback) {
        /**
         * Callback for CB.chat.dialog.getPublicOccupants()
         * @param {Object} error - The error object
         * @param {Object} response - the object with noccupants
         * @callback getPublicOccupantsCallback
         * */

        const ajaxParams = {
            url: Utils.getUrl(DIALOGS_API_URL, id, 'occupants'),
            data: params
        };

        this.service.ajax(ajaxParams, callback);
    }
};

module.exports = DialogProxy;
